// @ts-check

/**
 * @file Renders comments for a task.
 */

import _ from "gettext-universal/build/src/translate.js"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"
import {useLocalSearchParams} from "expo-router"
import {memo, useEffect} from "react"
import {View} from "react-native-propforge"
import {shapeComponent, ShapeComponent} from "set-state-compare/build/shape-component.js"
import buildRoutePath from "../../../src/shared/build-route-path"
import Button from "../../../src/components/button"
import errorMessage from "../../../src/shared/error-message"
import TaskThreadComment from "../../../src/components/tasks/thread-comment"
import {requiredRouteParam} from "../../../src/shared/route-params"
import ScreenTitle from "../../../src/components/screen-title"
import TaskSubnav from "../../../src/screens/tasks/subnav"
import {emptyTaskSubnavCounts, taskSubnavCountConfig, taskSubnavCountsFromTask} from "../../../src/screens/tasks/subnav-counts"
import {Task} from "../../../src/frontend-models/task"
import Text from "@kaspernj/api-maker/build/utils/text"
import useThemeColors, {useThemeName} from "../../../src/shared/use-theme-colors"

/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @import Comment from "../../../src/frontend-models/comment" */
/** @typedef {import("../../../src/screens/tasks/subnav-counts").TaskSubnavCounts} TaskSubnavCounts */
/** @type {Record<string, object>} */
const styles = {}

/** @typedef {{[key: string]: unknown}} TasksCommentsScreenProps */

/**
 * @typedef {object} TasksCommentsScreenState
 * @property {Comment[]} comments Loaded task comments.
 * @property {boolean} loading Whether comments are loading.
 * @property {{kind: "error", text: string} | null} status Screen status message.
 * @property {TaskSubnavCounts} subnavCounts Task subnav counts.
 * @property {Task | null} task Loaded task record.
 */

/**
 * Task comments list screen.
 * @augments {ShapeComponent<TasksCommentsScreenProps, TasksCommentsScreenState>}
 */
class TasksCommentsScreen extends ShapeComponent {
  /** @type {ReturnType<typeof useLocalSearchParams>} */
  params
  /** @type {string} */
  taskID
  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()
  /** @type {TasksCommentsScreenState} */
  state = {
    comments: /** @type {Comment[]} */ [],
    loading: true,
    status: /** @type {{kind: "error", text: string} | null} */ null,
    subnavCounts: emptyTaskSubnavCounts(),
    task: /** @type {Task | null} */ null
  }

  /** Initializes route params and comment loading. */
  setup() {
    useLocale()
    const params = useLocalSearchParams()

    const taskID = requiredRouteParam(params, "id", "Task ID")
    this.params = params
    this.taskID = taskID
    this.colors = useThemeColors()
    this.themeName = useThemeName()
    useEffect(() => {
      let cancelled = false
      const loadData = async () => {
        try {
          const task = await Task
            .where({id: taskID})
            .preload({
              comments: {user: true}
            })
            .withCount(taskSubnavCountConfig())
            .first()
          if (cancelled) return
          const comments = task
            ? task
              .commentsLoaded()
              .slice()
              .sort(compareCommentsByCreatedAtDesc)
            : []
          this.s.comments = comments
          this.s.subnavCounts = task ? taskSubnavCountsFromTask(task) : emptyTaskSubnavCounts()
          this.s.task = task
          this.s.loading = false
        } catch (error) {
          if (cancelled) return
          this.s.loading = false
          this.s.status = {
            kind: "error",
            text: errorMessage(error)
          }
        }
      }
      void loadData()
      return () => {
        cancelled = true
      }
    }, [taskID])
  }

  /**
   * Renders comments for the task.
   * @returns {ReactNode} Task comments screen content.
   */
  render() {
    const {comments, loading, status, subnavCounts, task} = this.s
    const {params, taskID} = this.tt
    const themeName = this.tt.themeName
    const colors = this.tt.colors
    const projectID = task ? task.projectId() || "" : ""
    return (
      <View
        style={styles.screen ||= {
          flex: 1
        }}
        testID="tasksCommentsScreen"
      >
        <ScreenTitle>{_("Comments")}</ScreenTitle>
        <View
          testID="tasksCommentsScreen/content"
        >
          <View
            style={styles.headerRow ||= {
              alignItems: "center",
              flexDirection: "row",
              justifyContent: "space-between",
              marginBottom: 16
            }}
            testID="tasksCommentsScreen/headerRow"
          >
            <Text
              style={styles.title ||= {
                fontSize: 24,
                fontWeight: 700
              }}
              testID="tasksCommentsScreen/title"
            >
              {_("Comments")}
            </Text>
            <Button
              href={buildRoutePath(
                params,
                projectID
                  ? `/comments/new?resourceType=Task&resourceId=${taskID}&projectId=${encodeURIComponent(projectID)}`
                  : `/comments/new?resourceType=Task&resourceId=${taskID}`
              )}
              iconName="plus"
              label={_("New comment")}
              testID="tasksCommentsScreen/newCommentButton"
            />
          </View>
          {task &&
            <TaskSubnav
              activeItem="comments"
              assignedUsersCount={subnavCounts.assignedUsersCount}
              commentsCount={subnavCounts.commentsCount}
              params={params}
              projectId={projectID}
              taskId={taskID}
              testID="tasksCommentsScreen/subnav"
              timelogsCount={subnavCounts.timelogsCount}
            />
          }
          {status &&
            <Text
              style={styles[`statusError-${themeName}`] ||= {
                color: colors.dangerText,
                fontSize: 14,
                fontWeight: 600,
                marginBottom: 16
              }}
              testID="tasksCommentsScreen/status"
            >
              {status.text}
            </Text>
          }
          {loading &&
            <Text
              style={styles[`loadingText-${themeName}`] ||= {
                color: colors.textMuted,
                fontSize: 16
              }}
              testID="tasksCommentsScreen/loadingText"
            >
              {_("Loading...")}
            </Text>
          }
          {!loading && comments.length === 0 &&
            <Text
              style={styles[`emptyText-${themeName}`] ||= {
                color: colors.textMuted,
                fontSize: 16
              }}
              testID="tasksCommentsScreen/emptyText"
            >
              {_("No comments found")}
            </Text>
          }
          {!loading &&
            comments.map((/** @type {Comment} */ comment) => {
              const commentID = String(comment.id())
              return (
                <TaskThreadComment
                  comment={comment}
                  key={commentID}
                  testID={`tasksCommentsScreen/comment-${commentID}`}
                />
              )
            })}
        </View>
      </View>
    )
  }
}

/**
 * Sorts newest comments first, matching the previous direct Comment query order.
 * @param {Comment} left Left comment.
 * @param {Comment} right Right comment.
 * @returns {number} Descending created-at comparison result.
 */
function compareCommentsByCreatedAtDesc(left, right) {
  const leftCreatedAt = left.createdAt()
  const rightCreatedAt = right.createdAt()

  return (rightCreatedAt?.getTime() || 0) - (leftCreatedAt?.getTime() || 0)
}

/** @type {NamedExoticComponent<TasksCommentsScreenProps>} */
const TasksCommentsScreenComponent = memo(shapeComponent(TasksCommentsScreen))

export default TasksCommentsScreenComponent
